import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getReportDetail, getReports, getRecordSpecificReports, createReport, deleteReport, updateReport, getReportTable, generateReport, favoriteReport, duplicateReport, generateInventoryTaxReport, generateSalesTaxReport, getTaxInfos, generateDailyReport } from "../api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";

export const useGetReports = (tab, selectedUserIds, search = '', page = 1, pageSize = 20, options = {}) => {
  const { enabled = true } = options;
  return useQuery({
    queryKey: ["reports", tab, selectedUserIds, search, page, pageSize],
    queryFn: () => getReports(tab, selectedUserIds, search, page, pageSize),
    enabled,
  });
};

export const useGetRecordSpecificReports = (recordType = 'account', enabled = true) => {
  return useQuery({
    queryKey: ["record-specific-reports", recordType],
    queryFn: () => getRecordSpecificReports(recordType),
    enabled,
  });
};

export const useGetReportTable = (id) => {
  return useQuery({
    queryKey: ["report-table", id],
    queryFn: () => getReportTable(id),
    enabled: !!id,
  });
};
export const useGetReportDetail = (id) => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ['report', id],
    queryFn: () => getReportDetail(id),
    enabled: !!id,
  });

  return { data, isLoading, isSuccess, isError, error };
};
export const useCreateReport = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (data) => createReport(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["reports"] });
      showNotification('success', 'Report created successfully!');
      navigate('/reports');


    },
    onError: (error) => {
      console.error("Error creating report:", error);

      // Show error notification
      let errorMessage = 'Error creating report. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useDeleteReport = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (id) => deleteReport(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["reports"] });

      // Show success notification
      showNotification('success', 'Report deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting report:", error);

      // Show error notification
      let errorMessage = 'Error deleting report. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useUpdateReport = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data }) => updateReport(id, data),
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["reports"] });
      await queryClient.invalidateQueries({ queryKey: ["report"] });

      // Show success notification
      showNotification('success', 'Report updated successfully!');

    },
    onError: (error) => {
      console.error("Error updating location:", error);

      // Show error notification
      let errorMessage = 'Error updating location. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useGenerateReport = () => {
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (id, format) => generateReport(id, format),
    onSuccess: (data) => {
      // Show success notification
      showNotification('success', 'Report generated successfully!');
    },
    onError: (error) => {
      console.error("Error generating report:", error);

      // Show error notification
      let errorMessage = 'Error generating report. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useFavoriteReport = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (report_id) => favoriteReport(report_id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["reports"] });
      showNotification('success', 'Report favorites updated successfully!');
    },
    onError: (error) => {
      console.error("Error updating favorite:", error);
      let errorMessage = 'Error updating favorite. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showNotification('danger', errorMessage);
    },
  });
};

export const useDuplicateReport = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (id) => duplicateReport(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["reports"] });
      showNotification('success', 'Report duplicated successfully!');
    },
    onError: (error) => {
      console.error("Error duplicating report:", error);
      let errorMessage = 'Error duplicating report. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showNotification('danger', errorMessage);
    },
  });
};

export const useGenerateInventoryTaxReport = () => {
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (data) => generateInventoryTaxReport(data),
    onSuccess: (data) => {
      // Show success notification
      showNotification('success', 'VIT Statement generated successfully!');
    },
    onError: (error) => {
      console.error("Error generating VIT statement:", error);

      // Show error notification
      let errorMessage = 'Error generating VIT statement. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useGenerateSalesTaxReport = () => {
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (data) => generateSalesTaxReport(data),
    onSuccess: (data) => {
      // Show success notification
      showNotification('success', 'Sales Tax Report generated successfully!');
    },
    onError: (error) => {
      console.error("Error generating sales tax report:", error);

      // Show error notification
      let errorMessage = 'Error generating sales tax report. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useGetTaxInfos = () => {
  return useQuery({
    queryKey: ["tax-infos"],
    queryFn: () => getTaxInfos(),
  });
};

export const useGenerateDailyReport = () => {
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (data) => generateDailyReport(data),
    onSuccess: (data) => {
      showNotification('success', 'Daily report generated successfully!');
    },
    onError: (error) => {
      console.error("Error generating daily report:", error);
      let errorMessage = 'Error generating daily report. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      showNotification('danger', errorMessage);
    },
  });
};